import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import {
  ColmecPartnerAgreement,
  ColmecPartnerAgreementsService
} from '../../services/colmec-partner-agreements.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Observable, Subject, first, takeUntil, of, catchError, throwError, forkJoin } from 'rxjs';
import { Customer, CustomerService, gungAddRemoveSpinner } from 'gung-standard';
import { S3Item, S3UploadService, OptionsListMulti, CommonModalService, JsonViewModal } from 'gung-common';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'colmec-create-edit-partner-agreement-modal',
    templateUrl: './colmec-create-edit-partner-agreement-modal.component.html',
    styleUrls: ['./colmec-create-edit-partner-agreement-modal.component.scss'],
    standalone: false
})
export class ColmecCreateEditPartnerAgreementModalComponent implements OnInit, OnDestroy {
  @Input() partnerAgreement: ColmecPartnerAgreement;
  private unsubscribe: Subject<void> = new Subject();

  public partnerAgreementForm: FormGroup;

  public customerList: Customer[];
  public partnerList: Customer[];
  public isCreate: boolean = false;
  public filePreviewUrl: string = '';
  public customerOptionsList: OptionsListMulti[] = [];
  public partnerOptionsList: OptionsListMulti[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    protected customerService: CustomerService,
    protected colmecPartnerAgreementsService: ColmecPartnerAgreementsService,
    protected s3UploadService: S3UploadService,
    protected modalService: CommonModalService
  ) {}

  ngOnInit(): void {
    this.isCreate = !!this.partnerAgreement ? false : true;
    this.customerService
      .getAllCustomersSubject()
      .pipe(first())
      .subscribe(customers => {
        this.customerList = customers.filter(customer => !customer.extra?.kus?.q_partner_boolean);
        this.partnerList = customers.filter(customer => !!customer.extra?.kus?.q_partner_boolean);
        this.initForm();
        this.buildCustomerDropdownOptionList();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected buildCustomerDropdownOptionList(): void {
    this.customerOptionsList = this.customerList.map(customer => {
      return {
        id: customer.id,
        name: customer.name,
        selected: this.isCreate ? false : customer.id === this.partnerAgreement.customerId
      };
    });

    this.partnerOptionsList = this.partnerList.map(customer => {
      return {
        id: customer.id,
        name: customer.name,
        selected: this.isCreate ? false : customer.id === this.partnerAgreement.partnerId
      };
    });
  }

  protected initForm(): void {
    this.partnerAgreementForm = new FormGroup({
      id: new FormControl(this.isCreate ? '' : this.partnerAgreement.id),
      name: new FormControl(this.isCreate ? '' : this.partnerAgreement.name),
      extra: new FormGroup({}),
      customerId: new FormControl(this.isCreate ? '' : this.partnerAgreement.customerId, [Validators.required]),
      partnerId: new FormControl(this.isCreate ? '' : this.partnerAgreement.partnerId, [Validators.required]),
      supplierId: new FormControl({ value: this.isCreate ? '' : this.partnerAgreement.supplierId, disabled: true }, []),
      servicePriceListId: new FormControl(this.isCreate ? '' : this.partnerAgreement.servicePriceListId, [
        Validators.required
      ]),
      agreementFiles: new FormControl([], this.isCreate ? [Validators.required] : []),
      agreementFileS3Uri: new FormControl(this.isCreate ? '' : this.partnerAgreement.agreementFileS3Uri)
    });
  }

  public setSelectedCustomerId(value: string[]): void {
    if (value?.length > 0) {
      this.partnerAgreementForm.get('customerId').setValue(value[0]);
    }
  }

  public setSelectedPartnerId(value: string[]): void {
    if (value?.length > 0) {
      this.partnerAgreementForm.get('partnerId').setValue(value[0]);
    }
  }

  public selectFiles(files: File[]): void {
    this.partnerAgreementForm.get('agreementFiles').setValue(files);
    if (files.length > 0) {
      const file = files[0];
      this.filePreviewUrl = URL.createObjectURL(file);
    } else {
      this.filePreviewUrl = '';
    }
  }

  public onSave({ target }): void {
    if (!this.partnerAgreementForm.valid) {
      this.partnerAgreementForm.markAllAsTouched();
      return;
    }

    gungAddRemoveSpinner(target);

    const rawForm: ColmecPartnerAgreement = this.partnerAgreementForm.getRawValue() as ColmecPartnerAgreement;

    forkJoin({
      customer: this.customerService.getCustomer(rawForm.customerId),
      partner: this.customerService.getCustomer(rawForm.partnerId)
    }).subscribe(({ customer, partner }) => {
      rawForm.customerName = customer.name;
      rawForm.partnerName = partner.name;

      const uploadedFiles: File[] = rawForm['agreementFiles'];

      delete rawForm['agreementFiles'];
      if (this.isCreate) {
        delete rawForm.id;
      }

      if (uploadedFiles.length > 0) {
        const file = uploadedFiles[0];
        this.s3UploadService
          .postImage(file)
          .pipe(first())
          .subscribe((document: S3Item) => {
            rawForm.agreementFileS3Uri = document.s3Uri;

            this.sendToBackend(rawForm)
              .pipe(
                first(),
                catchError((error: HttpErrorResponse, caught) => {
                  const data: JsonViewModal = {
                    title: 'ERROR',
                    subTitle:
                      'Error code: ' +
                      error.error.status +
                      '. There might already exist an agreement between this customer and partner.',
                    json: error.error.gungMessage || error.error.message,
                    typeString: true
                  };

                  this.modalService.openJSONModal(data);
                  return throwError(() => error);
                })
              )
              .subscribe(_ => {
                gungAddRemoveSpinner(target);
                this.activeModal.close('Closed Modal');
              });
          });
      } else {
        if (!this.isCreate) {
          this.sendToBackend(rawForm)
            .pipe(
              first(),
              catchError((error: HttpErrorResponse, caught) => {
                const data: JsonViewModal = {
                  title: 'ERROR',
                  subTitle: 'Error code: ' + error.error.status + '. Please contact Gung.',
                  json: error.error.gungMessage || error.error.message,
                  typeString: true
                };

                this.modalService.openJSONModal(data);
                return throwError(() => error);
              })
            )
            .subscribe(_ => {
              gungAddRemoveSpinner(target);
              this.activeModal.close('Closed Modal');
            });
        } else {
          gungAddRemoveSpinner(target);
        }
      }
    });
  }

  private sendToBackend(data: ColmecPartnerAgreement): Observable<any> {
    if (this.isCreate) {
      return this.colmecPartnerAgreementsService.createPartnerAgreement(data).pipe(first());
    } else {
      return this.colmecPartnerAgreementsService.updatePartnerAgreement(data).pipe(first());
    }
  }

  public onClose(): void {
    this.activeModal.dismiss('Closed Modal');
  }
}
